import React, { useState, useEffect, useRef } from 'react';
import { Form, ListGroup } from 'react-bootstrap';

const formatMentorOption = (mentor) => {
  if (!mentor) {
    return null;
  }
  const id = mentor._id || mentor.id;
  if (!id) {
    return null;
  }
  const displayName =
    mentor.firstName || mentor.lastName
      ? `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim()
      : mentor.name || '';
  if (!displayName) {
    return null;
  }
  return {
    mentorName: displayName,
    id
  };
};

const filterLocalOptions = (sourceList, query) => {
  const normalizedQuery = query.toLowerCase();
  return sourceList
    .map((item) => {
      if (typeof item === 'string') {
        return { mentorName: item, id: item };
      }
      return formatMentorOption(item);
    })
    .filter((option) => {
      return (
        option &&
        option.mentorName.toLowerCase().includes(normalizedQuery)
      );
    });
};

const Autocomplete = ({
  name,
  placeholder,
  dataSourge,
  value,
  setChanges,
  commission,
  setCommission,
  onSearch
}) => {
  const [options, setOptions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimer = useRef(null);
  const latestQuery = useRef('');

  useEffect(() => {
    setSearchValue(value || '');
  }, [value]);

  useEffect(() => {
    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, []);

  const runLocalSearch = (query) => {
    setOptions(filterLocalOptions(dataSourge || [], query));
  };

  const runBackendSearch = async (query) => {
    latestQuery.current = query;
    setIsSearching(true);
    try {
      const results = await onSearch(query);
      if (latestQuery.current !== query) {
        return;
      }
      const mappedResults = (results || [])
        .map(formatMentorOption)
        .filter((option) => option !== null);
      setOptions(mappedResults);
    } catch (searchError) {
      if (latestQuery.current === query) {
        setOptions([]);
      }
    } finally {
      if (latestQuery.current === query) {
        setIsSearching(false);
      }
    }
  };

  const handleFocusAutocomplete = () => {
    if (!searchValue || searchValue.length < 2) {
      return;
    }
    if (options.length > 0) {
      setSuggestionsVisible(true);
      return;
    }
    setSuggestionsVisible(true);
    if (typeof onSearch === 'function') {
      runBackendSearch(searchValue);
    } else {
      runLocalSearch(searchValue);
    }
  };

  const handleChangeAutocomplete = (event) => {
    const query = event.target.value;
    setSearchValue(query);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    if (query.length < 2) {
      latestQuery.current = '';
      setIsSearching(false);
      setSuggestionsVisible(false);
      setOptions([]);
      return;
    }
    setSuggestionsVisible(true);
    if (typeof onSearch === 'function') {
      setIsSearching(true);
      searchTimer.current = setTimeout(() => {
        runBackendSearch(query);
      }, 350);
    } else {
      runLocalSearch(query);
    }
  };

  const handleSelectOption = (selectedOption) => {
    setSearchValue(selectedOption.mentorName);
    setSuggestionsVisible(false);
    if (name === 'helper') {
      setCommission({ ...commission, helper: selectedOption.id });
    }
    if (name === 'mentor') {
      setCommission({ ...commission, mentor: selectedOption.id });
    }
    setChanges(true);
  };

  return (
    <div className="search-bar-container">
      <Form.Control
        type="text"
        name={name}
        placeholder={placeholder}
        value={searchValue || ''}
        onChange={handleChangeAutocomplete}
        onFocus={handleFocusAutocomplete}
        onBlur={() => {
          setTimeout(() => setSuggestionsVisible(false), 150);
        }}
        autoComplete="off"
        required={name === 'mentor'}
      />
      {suggestionsVisible && (
        <ListGroup className="suggestions-list">
          {isSearching ? (
            <ListGroup.Item disabled>Buscando...</ListGroup.Item>
          ) : options.length > 0 ? (
            options.map((option) => (
              <ListGroup.Item
                key={option.id}
                onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
                onClick={() => handleSelectOption(option)}
              >
                {`${option.mentorName}`}
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item disabled>Sin resultados</ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default Autocomplete;
